import { createContext, useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  authApi,
  type AuthUser,
  type SignInResult,
  type RecoveryLoginResult,
} from '@/lib/auth-api';
import { notifySessionExpired } from '@/lib/auth-refresh';
import { clearBootSessionHint } from '@/lib/boot-session-hint';
import { setTelemetryUser, clearTelemetryUser } from '@/lib/telemetry';
import { identifyIfNeeded } from '@/lib/attribution';

/**
 * Guest Trial Mode: set when a guest confirms a signup, consumed on the next
 * login to finalize the Postgres side of the upgrade. sessionStorage bridges the
 * signup → login redirect (SignupPage sends the user to /auth/login, not an
 * auto-login). See authApi.completeGuestUpgrade.
 */
const GUEST_UPGRADE_PENDING_KEY = 'fias_guest_upgrade_pending';

/**
 * The fias_session cookie has a 3h Cognito-bound TTL. Refresh well before
 * that so an actively-working user never sees a 401 mid-action. 2.5h leaves
 * a 30min margin for clock skew and request latency. We can't read the
 * cookie's actual issue time (httpOnly), so this is a cadence rather than a
 * precise deadline.
 */
const SESSION_REFRESH_INTERVAL_MS = 150 * 60 * 1000;

/**
 * When the tab regains focus/visibility, re-validate the session if it's been at
 * least this long since the last refresh. Far shorter than the routine cadence so
 * a session that died while the user was away surfaces the "please sign in" prompt
 * the moment they come back, instead of letting them click around a logged-out app.
 */
const SESSION_REVALIDATE_ON_RETURN_MS = 60 * 1000;

export interface AuthContextValue {
  user: AuthUser | null;
  isAuthenticated: boolean;
  /** True when the session is a Guest Trial Mode account (roles:['guest']). */
  isGuest: boolean;
  isLoading: boolean;
  /**
   * Password sign-in. For an MFA-enrolled user a correct password does NOT
   * establish a session — the result is `{ status: 'mfa_required' }` and the
   * caller must complete `mfaChallenge` (or `mfaRecoveryLogin`).
   */
  signIn: (email: string, password: string) => Promise<SignInResult>;
  /** Mint an anonymous guest trial session (Guest Trial Mode). */
  createGuestSession: () => Promise<void>;
  /**
   * Answer the TOTP challenge after `signIn` returned `mfa_required`.
   */
  mfaChallenge: (code: string) => Promise<void>;
  /**
   * Sign in with a single-use recovery code. Spends ONE code and nothing else —
   * MFA stays enabled and the authenticator secret is unchanged.
   */
  mfaRecoveryLogin: (
    email: string,
    password: string,
    recoveryCode: string,
    opId: string,
  ) => Promise<RecoveryLoginResult>;
  /**
   * UX-only hint: whether the current session's token says MFA is enrolled.
   * Not authoritative — the MFA settings card reads live status separately.
   */
  mfaEnrolled: boolean;
  /**
   * Reset client auth state WITHOUT a `/auth/logout` round-trip, for flows
   * where the backend already revoked the session server-side (MFA enroll /
   * disable / support reset global sign-out) and the user must re-authenticate.
   */
  handleReauthRequired: () => void;
  signUp: (
    email: string,
    password: string,
    inviteCode: string,
    username: string,
  ) => Promise<{ requiresConfirmation: boolean }>;
  confirmSignUp: (
    email: string,
    code: string,
    inviteCode?: string,
    username?: string,
  ) => Promise<void>;
  signOut: () => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  confirmForgotPassword: (email: string, code: string, newPassword: string) => Promise<void>;
  changePassword: (oldPassword: string, newPassword: string, totpCode?: string) => Promise<void>;
  refreshSession: () => Promise<void>;
  /** Whether the app is running in local development mode */
  isDevMode: boolean;
  /** Available dev users (empty array if not in dev mode) */
  devUsers: AuthUser[];
  /** Switch to a different dev user by index. Only works in dev mode. */
  switchDevUser: (index: number) => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const queryClient = useQueryClient();

  // Tracks whether the CURRENT session is a guest, so the (stable) signup/login
  // callbacks can read it without re-creating on every user change.
  const isGuestRef = useRef(false);
  useEffect(() => {
    isGuestRef.current = user?.roles?.includes('guest') ?? false;
  }, [user]);

  // Check for existing session on mount
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const currentUser = await authApi.getCurrentUser();
        setUser(currentUser);
        if (currentUser) {
          setTelemetryUser(currentUser);
        }
      } catch {
        // No valid session
        setUser(null);
        clearTelemetryUser();
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Hand the prerendered snapshot's pre-JS "probably signed in" guess back to
  // the app now that the real answer is in. Until this runs the root element
  // still carries `data-fias-session-hint`, which hides `#root` behind a
  // loading splash — correct for the boot window, a blank page for the rest of
  // the session if it is never cleared. Runs on BOTH outcomes: signed in (the
  // redirect has been rendered) and signed out (the hint was stale and the
  // marketing site underneath must be revealed). See lib/boot-session-hint.ts.
  //
  // An effect, not a call inside `initializeAuth`'s `finally`: effects run
  // after the commit that carries `isLoading: false`, so the reveal can never
  // land a frame ahead of the render that decided what to reveal.
  useEffect(() => {
    if (!isLoading) clearBootSessionHint();
  }, [isLoading]);

  const signIn = useCallback(async (email: string, password: string): Promise<SignInResult> => {
    const result = await authApi.signIn(email, password);
    if (result.status === 'authenticated') {
      setUser(result.user);
      setTelemetryUser(result.user);
      // If this login follows a guest → full upgrade, finalize the Postgres side
      // (clear is_guest + grant the welcome). Fire-and-forget: idempotent, and a
      // failure just retries on the next login — never block sign-in on it.
      // Only when a session was actually established (mfa_required has none).
      if (sessionStorage.getItem(GUEST_UPGRADE_PENDING_KEY)) {
        sessionStorage.removeItem(GUEST_UPGRADE_PENDING_KEY);
        void authApi.completeGuestUpgrade().catch(() => {
          // Best-effort; the account is fully usable regardless.
        });
      }
    }
    // `mfa_required` establishes no session — leave `user` untouched; the
    // caller drives the challenge step and `mfaChallenge` sets the user.
    return result;
  }, []);

  const createGuestSession = useCallback(async () => {
    const result = await authApi.createGuestSession();
    setUser(result.user);
    setTelemetryUser(result.user);
  }, []);

  const mfaChallenge = useCallback(async (code: string) => {
    const { user: challengedUser } = await authApi.mfaChallenge(code);
    setUser(challengedUser);
    setTelemetryUser(challengedUser);
  }, []);

  const mfaRecoveryLogin = useCallback(
    async (
      email: string,
      password: string,
      recoveryCode: string,
      opId: string,
    ): Promise<RecoveryLoginResult> => {
      const result = await authApi.mfaRecoveryLogin(email, password, recoveryCode, opId);
      if (result.status === 'authenticated') {
        setUser(result.user);
        setTelemetryUser(result.user);
      }
      return result;
    },
    [],
  );

  const handleReauthRequired = useCallback(() => {
    authApi.clearLocalSession();
    setUser(null);
    clearTelemetryUser();
    // Drop all cached server-state so the next user (or the re-authenticated
    // session) never sees the previous identity's cached data — including
    // security-posture queries (MFA status) whose keys are not user-scoped.
    // gcTime would otherwise serve stale cross-user data on a
    // shared browser.
    queryClient.clear();
  }, [queryClient]);

  const signUp = useCallback(
    async (email: string, password: string, inviteCode: string, username: string) => {
      return authApi.signUp(email, password, inviteCode, username);
    },
    [],
  );

  const confirmSignUp = useCallback(
    async (email: string, code: string, inviteCode?: string, username?: string) => {
      // If a guest is confirming a signup, the server upgrades their existing
      // usr_ id in PostConfirmation (reading the guest cookie). Mark it so the
      // next login finalizes the Postgres side (SignupPage redirects to login
      // rather than auto-logging-in, so the flag bridges that navigation).
      const wasGuest = isGuestRef.current;
      await authApi.confirmSignUp(email, code, inviteCode, username);
      if (wasGuest) {
        sessionStorage.setItem(GUEST_UPGRADE_PENDING_KEY, '1');
      }
    },
    [],
  );

  const signOut = useCallback(async () => {
    await authApi.signOut();
    setUser(null);
    clearTelemetryUser();
    // Clear all cached server-state on logout (see handleReauthRequired) so a
    // subsequent sign-in on the same browser starts from a clean cache.
    queryClient.clear();
  }, [queryClient]);

  const forgotPassword = useCallback(async (email: string) => {
    await authApi.forgotPassword(email);
  }, []);

  const confirmForgotPassword = useCallback(
    async (email: string, code: string, newPassword: string) => {
      await authApi.confirmForgotPassword(email, code, newPassword);
    },
    [],
  );

  const changePassword = useCallback(
    async (oldPassword: string, newPassword: string, totpCode?: string) => {
      await authApi.changePassword(oldPassword, newPassword, totpCode);
      // changePassword now revokes the session in Cognito and clears local
      // storage. Reflect that in context so callers re-mount as logged-out
      // and the user is forced to re-authenticate with the new password.
      setUser(null);
    },
    [],
  );

  const refreshSession = useCallback(async () => {
    // Actually rotate the session cookie (POST /auth/refresh) — the
    // previous implementation only re-fetched /auth/me, which kept
    // the client's view of the user fresh but did nothing to extend
    // the underlying cookie's expiry.
    const ok = await authApi.refreshSession();
    if (!ok) {
      setUser(null);
      clearTelemetryUser();
      notifySessionExpired();
    }
  }, []);

  // Acquisition attribution: link the anonymous visitor id to the signed-in
  // user (once per user per browser — identifyIfNeeded self-guards and
  // retries on the next authenticated load if the POST fails).
  useEffect(() => {
    if (user?.userId) {
      void identifyIfNeeded(user.userId);
    }
  }, [user?.userId]);

  // Mirror any session-expiry signal into auth state. The api 401 interceptor,
  // streaming hooks, and the proactive refresh below all dispatch
  // `fias:session-expired` when a session can't be recovered; without reacting to
  // it here, a 401 from the api layer pops the "session expired" modal but leaves
  // `user` set — so ProtectedRoute keeps rendering and the app looks
  // logged-in-but-broken (you can click around, but every action silently fails).
  // Clearing `user` makes the whole app consistently reflect the logged-out state.
  useEffect(() => {
    const onExpired = () => {
      setUser(null);
      clearTelemetryUser();
    };
    window.addEventListener('fias:session-expired', onExpired);
    return () => window.removeEventListener('fias:session-expired', onExpired);
  }, []);

  // Proactive refresh while signed in. setInterval gets throttled or
  // suspended in background tabs by every modern browser, so we also
  // re-validate on focus/visibility when the tab returns. The user pays
  // no perceptible cost — refresh is a single cookie-only POST.
  useEffect(() => {
    if (!user) return;

    let lastRefreshAt = Date.now();

    const refreshNow = async () => {
      lastRefreshAt = Date.now();
      const ok = await authApi.refreshSession();
      if (!ok) {
        setUser(null);
        clearTelemetryUser();
        notifySessionExpired();
      }
    };

    const intervalId = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        void refreshNow();
      }
      // If hidden, skip — the focus/visibility handler below catches us
      // up when the user returns to the tab.
    }, SESSION_REFRESH_INTERVAL_MS);

    // On returning to the tab/window, re-validate promptly so an expired
    // session surfaces the sign-in prompt immediately rather than after the
    // next routine refresh tick or a failed action. Throttled so rapid tab
    // switches don't spam refreshes.
    const revalidateOnReturn = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastRefreshAt >= SESSION_REVALIDATE_ON_RETURN_MS) {
        void refreshNow();
      }
    };

    document.addEventListener('visibilitychange', revalidateOnReturn);
    window.addEventListener('focus', revalidateOnReturn);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', revalidateOnReturn);
      window.removeEventListener('focus', revalidateOnReturn);
    };
  }, [user]);

  const switchDevUser = useCallback(async (index: number) => {
    const newUser = await authApi.switchDevUser(index);
    if (newUser) {
      setUser(newUser);
      setTelemetryUser(newUser);
    }
  }, []);

  const value: AuthContextValue = {
    user,
    isAuthenticated: !!user,
    isGuest: user?.roles?.includes('guest') ?? false,
    isLoading,
    signIn,
    createGuestSession,
    mfaChallenge,
    mfaRecoveryLogin,
    mfaEnrolled: user?.mfaEnrolled ?? false,
    handleReauthRequired,
    signUp,
    confirmSignUp,
    signOut,
    forgotPassword,
    confirmForgotPassword,
    changePassword,
    refreshSession,
    isDevMode: authApi.isDevMode(),
    devUsers: authApi.getDevUsers(),
    switchDevUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
